import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, UnstyledButton } from '@mantine/core';
import type { PlacementAnswerResponse } from '@nelnela/contracts';
import { api } from '../lib/api';
import {
  answer,
  bandLabel,
  climbSteps,
  currentWord,
  dots,
  edgeLabel,
  emptyRun,
  trail,
  transcript,
  type Run,
  type Step,
} from '../lib/placement';
import { useStudy } from '../app/study';

/**
 * The placement wizard (M10-iii), to M10-α's three frames: why → the climb → the edge.
 *
 * THE SCREEN IS WIRING. Every decision lives in `lib/placement.ts` and is tested there, because
 * the web suite is node with no DOM (M6c's gap). What is here is layout, two network calls, and
 * the α decisions that are *about* rendering and could not live anywhere else.
 *
 * The α calls this file is obeying, all binding:
 *   · THE DEALT WORD IS PLAIN DOM — no `.nn-reading`, no tap targets, no gloss. A gloss would
 *     answer the question being asked, and a tap target would turn a placement question into a
 *     lookup. This is the one screen where Georgian is deliberately bare.
 *   · BOTH ANSWERS CARRY EQUAL WEIGHT. Neither is ember: ember is the acting accent and would
 *     read as the right answer. "don't" is not a failure, it is half the instrument.
 *   · THE TRAIL NEVER PROMISES A LENGTH. Climbed bands settle, the current one breathes, exactly
 *     one dashed ghost sits ahead — and no total, ever. The climb ends when a band falls under
 *     threshold or the catalog thins, so a progress bar would have to invent its denominator.
 *   · QUIT IS THREE WORDS AND NO DIALOG. "Quit — nothing saved" is the whole contract, and it is
 *     true of the database as well: the routes are stateless and an unfinished band is never sent.
 *   · FULL-SCREEN, NO NAV. A flow, not a tab — chrome would invite wandering mid-run. Desktop is
 *     the same single centred column for the same reason.
 */

const HIT = 56; // α: the two answer targets, thumb zone
const DOT = 6;

export function Placement() {
  const study = useStudy();
  const navigate = useNavigate();
  const profileId = study.me.profiles[0]?.id ?? null;

  const [phase, setPhase] = useState<'why' | 'climb' | 'edge'>('why');
  const [run, setRun] = useState<Run>(emptyRun);
  const [verdict, setVerdict] = useState<PlacementAnswerResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** one request at a time: the deal effect must not fire twice for the same empty deal */
  const inFlight = useRef(false);

  /**
   * Ask for the next band, or finish.
   *
   * Both calls carry the WHOLE transcript because the server keeps none of it. The finish call is
   * made from here rather than from a button: the climb ends when the server says `done`, and the
   * learner never chooses to stop — they either answer or quit.
   */
  useEffect(() => {
    if (phase !== 'climb' || run.deal !== null || inFlight.current) return;
    inFlight.current = true;
    let cancelled = false;
    void (async () => {
      try {
        if (!profileId) throw new Error('no profile');
        const bands = transcript(run);
        const got = await api.placementNext({ profileId, bands });
        if (cancelled) return;
        if (got.done || !got.level || !got.items) {
          const said = await api.placementAnswer({ profileId, bands });
          if (cancelled) return;
          setVerdict(said);
          setPhase('edge');
          return;
        }
        setRun((r) => ({ ...r, deal: { level: got.level!, items: got.items!, answers: [] } }));
      } catch {
        if (!cancelled) setError('Could not reach the server. Nothing was saved.');
      } finally {
        inFlight.current = false;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [phase, run, profileId]);

  const say = useCallback((said: boolean) => setRun((r) => answer(r, said)), []);

  // Desktop: arrow keys answer. The hint under the buttons says so — a shortcut nobody is told
  // about is not an affordance.
  useEffect(() => {
    if (phase !== 'climb') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') say(false);
      else if (e.key === 'ArrowRight') say(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [phase, say]);

  if (phase === 'why') {
    return (
      <Frame>
        {/* α: "Not now" dismisses — first-run continues to Today and the wizard waits in Me. It
            is the same door as Quit, and it is open before the run as well as during it. */}
        <div>
          <UnstyledButton
            onClick={() => navigate('/')}
            style={{ fontSize: 13, color: 'var(--nn-muted)' }}
          >
            Not now
          </UnstyledButton>
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ textAlign: 'center', display: 'grid', gap: 14 }}>
          <div style={{ fontSize: 26, fontWeight: 600, color: 'var(--nn-text-strong)' }}>
            Find your level
          </div>
          {/* One sentence of promise. No exam framing, and no preview of how long the climb is —
              the climb does not know. */}
          <div style={{ fontSize: 15, lineHeight: 1.6, color: 'var(--nn-muted)', maxWidth: 320 }}>
            A few quick rounds of “know it / don’t” — so practice starts right at your edge.
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'grid', gap: 12, justifyItems: 'center' }}>
          <Button
            onClick={() => setPhase('climb')}
            styles={{
              root: {
                background: 'var(--nn-accent)',
                color: 'var(--nn-on-accent)',
                height: HIT,
                width: '100%',
                borderRadius: 12,
                fontSize: 16,
              },
            }}
          >
            Start
          </Button>
          <div style={{ fontSize: 12, color: 'var(--nn-faint)' }}>
            2–4 minutes · re-run anytime from Me
          </div>
        </div>
      </Frame>
    );
  }

  if (phase === 'edge') {
    const label = edgeLabel(verdict?.band ?? null);
    return (
      <Frame>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'grid', gap: 18, justifyItems: 'center', textAlign: 'center' }}>
          {/* The trail settles: every step solid, the last one ember, and no ghost — the climb is
              over, not failed. */}
          <Trail steps={trail(run, null)} settledEnd />
          <div style={{ fontSize: 13, color: 'var(--nn-muted)' }}>your edge, for now</div>
          <div style={{ fontSize: 24, fontWeight: 600, color: 'var(--nn-text-strong)' }}>
            {label ?? 'not enough to say yet'}
          </div>
          <div style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--nn-muted)', maxWidth: 320 }}>
            {label
              ? 'We’ll give you practice right at your edge. It’s an estimate — it moves as you do.'
              : 'You stopped before a whole round was answered, so nothing changed.'}
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'grid', gap: 12, justifyItems: 'center' }}>
          <Button
            onClick={() => navigate('/')}
            styles={{
              root: {
                background: 'var(--nn-accent)',
                color: 'var(--nn-on-accent)',
                height: HIT,
                width: '100%',
                borderRadius: 12,
                fontSize: 16,
              },
            }}
          >
            Done
          </Button>
          <div style={{ fontSize: 12, color: 'var(--nn-faint)' }}>
            run it again from Me, whenever
          </div>
        </div>
      </Frame>
    );
  }

  const word = currentWord(run);
  const round = dots(run);
  return (
    <Frame>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {/* No confirm dialog, by α — there is nothing to lose, and a dialog would imply there is. */}
        <button
          type="button"
          onClick={() => navigate('/')}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            font: 'inherit',
            fontSize: 13,
            color: 'var(--nn-muted)',
            cursor: 'pointer',
          }}
        >
          Quit — nothing saved
        </button>
        {/* The band chip doubles as the "where am I" answer, and rises with the climb. */}
        <div
          style={{
            fontSize: 10,
            letterSpacing: '.08em',
            fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
            color: 'var(--nn-faint)',
          }}
        >
          {run.deal ? bandLabel(run.deal.level) : ''}
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 26 }}>
        <Trail steps={climbSteps(run)} />
      </div>

      <div style={{ flex: 1 }} />
      <div style={{ textAlign: 'center', minHeight: 44 }}>
        {error ? (
          <div style={{ fontSize: 14, color: 'var(--nn-muted)' }}>{error}</div>
        ) : (
          /* Plain DOM. Not `.nn-reading`, no marks, no tap target — see the header. */
          <div style={{ fontSize: 34, color: 'var(--nn-text-strong)' }}>{word ?? '…'}</div>
        )}
      </div>
      <div style={{ flex: 1 }} />

      {round && (
        <div
          style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 20 }}
          aria-label={`${round.filled} of ${round.total} answered`}
        >
          {Array.from({ length: round.total }, (_, i) => (
            <span
              key={i}
              style={{
                width: DOT,
                height: DOT,
                borderRadius: DOT,
                background: i < round.filled ? 'var(--nn-muted)' : 'var(--nn-hairline)',
              }}
            />
          ))}
        </div>
      )}

      {/* Equal weight, both of them. Neither is ember; "don't" is not a failure. */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <AnswerButton label="don’t" onClick={() => say(false)} disabled={!word} />
        <AnswerButton label="know it" onClick={() => say(true)} disabled={!word} />
      </div>
      <div
        style={{
          marginTop: 10,
          textAlign: 'center',
          fontSize: 11,
          fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
          color: 'var(--nn-faint)',
        }}
      >
        ← don’t · → know it
      </div>
    </Frame>
  );
}

/**
 * Full-screen, single column, centred, and capped at a phone measure even on desktop.
 *
 * α: "the wizard is a full-screen flow, chrome would invite wandering mid-run". The app's nav is
 * not rendered here — this is the one route that takes the whole screen.
 */
function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        background: 'var(--nn-wash)',
        // `100dvh`, not `100%`: a flow renders straight into the router outlet with no
        // `AppShell.Main` above it, so there is no parent height for a percentage to resolve
        // against — the column collapses to its content and the spacers below stop spacing.
        minHeight: '100dvh',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 420,
          minHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          padding: '20px 20px 28px',
        }}
      >
        {children}
      </div>
    </div>
  );
}

function AnswerButton({
  label,
  onClick,
  disabled,
}: {
  label: string;
  onClick: () => void;
  disabled: boolean;
}) {
  return (
    <Button
      // BLUR AFTER ANSWERING, and this is α's "equal weight" rather than fussiness. A click leaves
      // focus on the button; the next ARROW KEY flips the browser into keyboard modality, and
      // `:focus-visible` then paints an ember ring around whichever answer was last clicked. One
      // of two supposedly equal choices wearing the accent reads as the right one — which is the
      // exact thing "ember is not a reward" forbids. Seen on screen, not reasoned about.
      onClick={(e) => {
        e.currentTarget.blur();
        onClick();
      }}
      disabled={disabled}
      styles={{
        root: {
          height: HIT,
          borderRadius: 12,
          background: 'var(--nn-well)',
          color: 'var(--nn-text)',
          border: '1px solid var(--nn-hairline)',
          fontSize: 16,
          fontWeight: 500,
          // The gap between bands is a round trip with nothing to answer. Mantine's own disabled
          // styling loses to `styles.root`, so without this the pair looks live while it is not.
          '&:disabled': { background: 'var(--nn-well)', color: 'var(--nn-faint)', opacity: 0.5 },
        },
      }}
    >
      {label}
    </Button>
  );
}

/**
 * The settled-steps trail. Filled for what was climbed, a lit fill on the current band, one dashed
 * outline ahead — and no total anywhere.
 *
 * `settledEnd` is the result screen: the climb is over, so the last step goes solid ember and the
 * ghost is gone.
 */
function Trail({ steps, settledEnd = false }: { steps: Step[]; settledEnd?: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      {steps.map((s, i) => {
        const last = i === steps.length - 1;
        const ember = s.state === 'current' || (settledEnd && last);
        return (
          <span
            key={s.level}
            style={{
              width: 26,
              height: 6,
              borderRadius: 3,
              // α: settled steps gray, current/final ember at .14 / .55.
              background: ember
                ? settledEnd && last
                  ? 'color-mix(in srgb, var(--nn-accent) 55%, transparent)'
                  : 'color-mix(in srgb, var(--nn-accent) 14%, transparent)'
                : s.state === 'ghost'
                  ? 'transparent'
                  : 'var(--nn-hairline-lit)',
              border:
                s.state === 'ghost'
                  ? '1px dashed var(--nn-hairline)'
                  : ember && !settledEnd
                    ? '1px solid color-mix(in srgb, var(--nn-accent) 45%, transparent)'
                    : 'none',
              boxSizing: 'border-box',
            }}
          />
        );
      })}
    </div>
  );
}
